/**
 * DriverEarningsMorph — top-center earnings pill that *morphs* into the
 * full Earnings Hub drawer, exactly like the Rider booking pill.
 *
 * One DOM element. The collapsed pill and the expanded drawer share
 * the same `layoutId` so Framer Motion interpolates width / height /
 * border-radius / position. There is no "open a separate drawer" jump —
 * the pill literally grows into the sheet, and shrinks back on close.
 *
 * Replaces the old DriverEarningsHubDrawer + standalone pill button in
 * DriverMarketplace.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, DollarSign, Calendar, Clock, Layers, TrendingUp, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { DURATION, EASE } from "@/shared/motion/tokens";
import { surfaceStyle } from "@/shared/motion/surface";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";

// Apple-style ease curves — no spring overshoot, no bounce.
const APPLE_EASE_OUT = [0.32, 0.72, 0, 1];
const CONTENT_ENTER_DELAY_MS = 280;
const CONTENT_EXIT_MS = 180;
const UNMOUNT_GRACE_MS = 560;

/**
 * Variants drive the 2-stage morph between pill and drawer. Framer
 * applies each variant's nested `transition` when interpolating TO
 * that variant — so "open" (animate: drawer) uses the drawer
 * variant's per-property delays (width FIRST, height delayed), and
 * "close" (exit: pill) uses the pill variant's per-property delays
 * (height FIRST, width delayed). Clean width-then-height on open;
 * height-then-width on close; no keyframe interpolation overshoot.
 */
const EARNINGS_VARIANTS = {
  pill: {
    width: "110px",
    height: "48px",
    borderRadius: "9999px",
    transition: {
      // Close: height shrinks first, width follows after a beat.
      height:       { duration: 0.28, ease: APPLE_EASE_OUT },
      width:        { duration: 0.24, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderRadius: { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
    },
  },
  drawer: {
    width: "100vw",
    height: "88dvh",
    borderRadius: "32px",
    transition: {
      // Open: width grows first, height drops down after a beat.
      width:        { duration: 0.26, ease: APPLE_EASE_OUT },
      height:       { duration: 0.32, ease: APPLE_EASE_OUT, delay: 0.20 },
      borderRadius: { duration: 0.18, ease: APPLE_EASE_OUT },
    },
  },
};

export default function DriverEarningsMorph({
  todayEarnings,
  open,
  onOpenChange,
  hidden = false,
}) {
  const [data, setData] = useState(null);
  const [blocks, setBlocks] = useState(null);

  // Open/close choreography mirrors the Rider Account drawer:
  //   open  → layoutId morph, then 260ms later content fades+slides in
  //   close → content fades+slides OUT first, then layoutId collapse
  const [showContent, setShowContent] = useState(false);
  const [renderInner, setRenderInner] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [e, b] = await Promise.all([
        api.get("/driver/earnings"),
        api.get("/driver/my-blocks"),
      ]);
      setData(e.data); setBlocks(b.data);
    } catch (ex) {
      toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load earnings");
    }
  }, []);

  // Load (or refresh) when opened.
  useEffect(() => { if (open) load(); }, [open, load]);

  // Choreography state machine driven by `open`.
  useEffect(() => {
    if (open) {
      setClosing(false);
      setRenderInner(true);
      const t = setTimeout(() => setShowContent(true), CONTENT_ENTER_DELAY_MS);
      return () => clearTimeout(t);
    }
    setShowContent(false);
    setClosing(false);
    const t = setTimeout(() => setRenderInner(false), UNMOUNT_GRACE_MS);
    return () => clearTimeout(t);
  }, [open]);

  const handleClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    setShowContent(false); // fades the body out in parallel with the height shrink
    onOpenChange?.(false); // fire immediately — exit transition handles the height-then-width choreography itself
  }, [closing, onOpenChange]);

  // Body scroll lock + ESC while expanded.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, handleClose]);

  // Cleanup pending close timer on unmount.
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  // Drag-to-dismiss — the earnings drawer is top-anchored so swiping UP
  // pulls it back toward the originating pill. No scroll guard: the
  // drawer content is short (today + 7-day summary cards).
  const drawerScrollRef = useRef(null);
  const drag = useDragDismiss({
    onDismiss: handleClose,
    origin: "top",
    open,
    scrollRef: drawerScrollRef,
  });

  return (
    <>
      {/* Collapsed pill — always rendered at top-center. The expanded
          drawer sits at a higher z-index, so during open the pill is
          simply covered, not unmounted. No layoutId on either side —
          the expansion is hand-choreographed for the 2-stage feel. */}
      <motion.div
        className="fixed left-1/2 pointer-events-none"
        style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)", zIndex: 32, x: "-50%" }}
        animate={{ opacity: hidden ? 0 : 1 }}
        transition={{ duration: DURATION.fast, ease: EASE.out }}
      >
        <motion.button
          type="button"
          onClick={() => onOpenChange?.(true)}
          className="pointer-events-auto h-12 px-5 rounded-full bg-[#0A1128] text-white shadow-[0_8px_28px_rgba(10,17,40,0.28)] inline-flex items-center overflow-hidden"
          aria-label="Open earnings hub"
          data-testid="driver-map-earnings-pill"
          style={{ borderRadius: 9999, willChange: "transform" }}
          whileTap={{ scale: 0.95 }}
          transition={{ duration: 0.14, ease: APPLE_EASE_OUT }}
        >
          <span className="font-serif text-[18px] leading-none whitespace-nowrap" data-testid="driver-map-earnings-value">
            ${todayEarnings.toFixed(2)}
          </span>
        </motion.button>
      </motion.div>

      {/* Backdrop + expanded drawer (hand-choreographed 2-stage) */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="earn-backdrop"
              className="fixed inset-0 z-40 bg-black/35"
              style={{ opacity: drag.backdropOpacity }}
              initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
              animate={{ opacity: 1, backdropFilter: "blur(6px)" }}
              exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
              transition={{ duration: 0.22, ease: APPLE_EASE_OUT }}
              onClick={handleClose}
              data-testid="driver-earnings-backdrop"
            />
            {/* Expanded morphing drawer. Sequence:
                  OPEN  — width grows from pill-width → marketplace-card width → full width (0-300ms),
                          THEN height grows from pill-height → 88dvh (220-520ms).
                  CLOSE — height shrinks first (0-260ms), THEN width shrinks
                          back to the pill (260-460ms).
                Position is fixed top-anchored so the drawer literally
                expands FROM the pill's spot, not from the bottom. */}
            <motion.div
              key="earn-expanded"
              className="fixed left-1/2 z-50 bg-[#F9F7F4] text-[#0A1128] overflow-hidden"
              style={{
                top: "calc(env(safe-area-inset-top, 0px) + 12px)",
                x: "-50%",
                y: drag.value,
                transformOrigin: "top center",
                willChange: "width, height, border-radius",
                ...surfaceStyle("drawer"),
              }}
              variants={EARNINGS_VARIANTS}
              initial="pill"
              animate="drawer"
              exit="pill"
              role="dialog"
              aria-modal="true"
              data-testid="driver-earnings-drawer"
              drag={drag.motionProps.drag}
              dragConstraints={drag.motionProps.dragConstraints}
              dragElastic={drag.motionProps.dragElastic}
              dragMomentum={drag.motionProps.dragMomentum}
              onDragStart={drag.motionProps.onDragStart}
              onDrag={drag.motionProps.onDrag}
              onDragEnd={drag.motionProps.onDragEnd}
            >
              {/* Grabber + close — close fades with content so the
                  header collapses as one piece on the way out. */}
              <motion.div
                animate={{ opacity: showContent ? 1 : 0 }}
                transition={{ duration: showContent ? 0.22 : 0.18, ease: APPLE_EASE_OUT }}
                className="relative"
              >
                <div className="flex items-center justify-center pt-3 pb-1">
                  <span className="w-10 h-1 rounded-full bg-[#0A1128]/15" />
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="absolute top-3 right-4 w-9 h-9 rounded-full bg-white border border-black/[0.06] shadow-sm inline-flex items-center justify-center text-[#0A1128] hover:bg-[#F1ECE3] transition-transform duration-150 active:scale-95"
                  aria-label="Close earnings"
                  data-testid="driver-earnings-close"
                >
                  <X size={16} strokeWidth={1.8} />
                </button>
              </motion.div>
              {/* Body — fades+nudges in once the drawer reaches full
                  size, and out before the drawer starts shrinking. */}
              <motion.div
                ref={drawerScrollRef}
                animate={{ opacity: showContent ? 1 : 0, y: showContent ? 0 : 6 }}
                transition={{ duration: showContent ? 0.22 : 0.18, ease: APPLE_EASE_OUT }}
                className="overflow-y-auto px-5 sm:px-6 pb-[max(env(safe-area-inset-bottom),32px)]"
                style={{ maxHeight: "calc(88dvh - 24px)" }}
              >
                {renderInner ? (
                  data ? (
                    <Body data={data} blocks={blocks} />
                  ) : (
                    <div className="py-12 text-center text-[#515E7B]"><Loader2 size={16} className="inline animate-spin mr-2"/> Loading earnings…</div>
                  )
                ) : null}
              </motion.div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ============================ Body / sub-components ============================ */
function Body({ data, blocks }) {
  const lifetime = data.lifetime || {};
  const hoursWorked = (lifetime.blocks_completed || 0) > 0 ? (lifetime.stops_completed * 18 / 60).toFixed(1) : "0.0";
  const completed = (blocks?.completed || []);
  return (
    <div className="space-y-5 pt-3">
      <header className="px-1" data-testid="earnings-header">
        <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Earnings Hub</div>
        <h1 className="font-serif font-light text-[48px] sm:text-[56px] leading-[1.05] text-[#0A1128] mt-1" data-testid="earnings-lifetime-headline">
          ${(lifetime.gross || 0).toFixed(2)}
        </h1>
        <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mt-1">Lifetime earnings</div>
        <div className="text-[12px] text-[#515E7B]/80 mt-3">Updated {new Date(data.as_of).toLocaleString()}</div>
      </header>

      <div className="grid grid-cols-2 gap-2" data-testid="earnings-hero-pair">
        <HeroBox label="Today"     v={data.today?.gross} testId="earn-hero-today" />
        <HeroBox label="This week" v={data.week?.gross}  testId="earn-hero-week" />
      </div>

      <div className="grid grid-cols-2 gap-2" data-testid="earnings-period-grid">
        <PeriodBox label="This month" v={data.month?.gross}    testId="earn-period-month" />
        <PeriodBox label="Lifetime"   v={data.lifetime?.gross} testId="earn-period-lifetime" />
      </div>

      <Section title="Block history">
        {completed.length === 0 ? (
          <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-6 text-center" data-testid="earnings-history-empty">
            <div className="font-serif text-[18px] text-[#0A1128]">No completed blocks yet</div>
            <p className="text-[12px] text-[#515E7B] mt-1.5">Earnings will accumulate here as you complete work.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {completed.map((b) => <BlockHistoryRow key={b.id} block={b} />)}
          </div>
        )}
      </Section>

      <Section title="Performance">
        <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5 grid grid-cols-2 gap-3" data-testid="earnings-metrics">
          <Metric icon={<Layers size={13}/>}    label="Total blocks"    value={lifetime.blocks_completed || 0} />
          <Metric icon={<Calendar size={13}/>}  label="Total stops"     value={lifetime.stops_completed || 0} />
          <Metric icon={<Clock size={13}/>}     label="Hours worked"    value={hoursWorked} />
          <Metric icon={<TrendingUp size={13}/>}label="Avg block value" value={`$${(lifetime.avg_block_value || 0).toFixed(2)}`} />
          <Metric icon={<DollarSign size={13}/>}label="Avg per hour"    value={`$${(lifetime.avg_per_hour || 0).toFixed(2)}`} />
          <Metric icon={<DollarSign size={13}/>}label="Tips"            value={`$${(lifetime.tips || 0).toFixed(2)}`} />
          <Metric icon={<DollarSign size={13}/>}label="Adjustments"     value={`$${(lifetime.adjustments || 0).toFixed(2)}`} />
          <Metric icon={<DollarSign size={13}/>}label="Bonuses"         value={`$${(lifetime.bonuses || 0).toFixed(2)}`} />
        </div>
      </Section>

      <div className="rounded-3xl border border-dashed bg-[#F1ECE3]/60 border-[#0A1128]/15 p-5" data-testid="earnings-payouts-placeholder">
        <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] mb-1">
          <DollarSign size={12}/> Payouts coming soon
        </div>
        <p className="text-[13px] text-[#515E7B] leading-relaxed">
          Stripe Connect for instant payouts is launching shortly. Your earnings will accumulate here in the meantime.
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="px-1 text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B]/85">{title}</div>
      {children}
    </div>
  );
}

function PeriodBox({ label, v, testId }) {
  return (
    <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">{label}</div>
      <div className="font-serif font-light text-[26px] leading-none text-[#0A1128] mt-2">${(v || 0).toFixed(2)}</div>
    </div>
  );
}

function HeroBox({ label, v, testId }) {
  return (
    <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#515E7B]">{label}</div>
      <div className="font-serif font-light text-[36px] leading-none text-[#0A1128] mt-2">${(v || 0).toFixed(2)}</div>
    </div>
  );
}

function Metric({ icon, label, value }) {
  return (
    <div className="rounded-2xl bg-[#F9F7F4] border border-black/[0.05] px-3 py-3">
      <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-[#515E7B]">{icon}{label}</div>
      <div className="font-serif text-[20px] text-[#0A1128] mt-1 leading-none">{value}</div>
    </div>
  );
}

function BlockHistoryRow({ block }) {
  const [open, setOpen] = useState(false);
  const stops = block.stops || [];
  const pickups = stops.filter((s) => s.kind === "pickup");
  const perLeg = pickups.length ? (block.pay_dollars / pickups.length) : 0;
  return (
    <div className="rounded-3xl bg-white border border-black/[0.06] shadow-sm overflow-hidden" data-testid={`earnings-block-${block.id}`}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full text-left px-5 py-4 flex items-center gap-3 transition-transform duration-150 active:scale-[0.99]"
        data-testid={`earnings-block-${block.id}-toggle`}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">
            {block.area_label || block.sector_name || "Block"}
          </div>
          <div className="font-serif text-[22px] text-[#0A1128] mt-0.5 leading-none">${block.pay_dollars.toFixed(2)}</div>
          <div className="text-[11.5px] text-[#515E7B] mt-1">Completed {block.date || "—"} · {block.stops_count} stops</div>
        </div>
        <motion.div animate={{ rotate: open ? 90 : 0 }} transition={{ duration: DURATION.fast, ease: EASE.out }}>
          <ChevronRight size={16} className="text-[#515E7B]"/>
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="legs"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: DURATION.normal, ease: EASE.out }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-black/[0.06] pt-3 space-y-1.5" data-testid={`earnings-block-${block.id}-legs`}>
              {pickups.length === 0 ? (
                <div className="text-[12px] text-[#515E7B]">Per-leg breakdown unavailable for this block.</div>
              ) : (
                pickups.map((p, i) => {
                  const drop = stops.find((s) => s.kind === "dropoff" && s.user_id === p.user_id);
                  return (
                    <div key={p.id} className="flex items-center justify-between text-[12.5px]">
                      <div className="text-[#0A1128] truncate pr-3">
                        Pickup {i + 1} · {p.rider_name || "Rider"}
                        {drop && drop.address && (<><br/><span className="text-[11px] text-[#515E7B]">→ {drop.address}</span></>)}
                      </div>
                      <div className="text-[#0A1128] font-semibold whitespace-nowrap">${perLeg.toFixed(2)}</div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
